// iPad field mode: capture assessments at an event with no connection. Works signed out and offline;
// protected by the field PIN instead (see lib/fieldStore.ts). Syncing needs a connection and a signed-in
// staff user; synced records arrive in the office as "Synced from iPad".
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { MeasurementFields, Results, scoreLive, type Measurements } from '../components/Intake.tsx';
import { DevBanner, ErrorBox } from '../components/Layout.tsx';
import { get } from '../lib/api.ts';
import * as fs from '../lib/fieldStore.ts';
import type { Capture, EventPackage, RosterPerson, Walkin } from '../lib/fieldStore.ts';
import { localToday } from './Participants.tsx';

// ---------------------------------------------------------------- shared bits
/** Can we reach the NGF server? (navigator.onLine only knows about the iPad's Wi-Fi, not the server.) */
function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    let live = true;
    const probe = async () => {
      if (!navigator.onLine) return live && setOnline(false);
      const ctl = new AbortController();
      const t = setTimeout(() => ctl.abort(), 4000);
      try { const r = await fetch('/healthz', { cache: 'no-store', signal: ctl.signal }); if (live) setOnline(r.ok); }
      catch { if (live) setOnline(false); } finally { clearTimeout(t); }
    };
    void probe();
    const every = setInterval(() => void probe(), 20_000);
    window.addEventListener('online', probe); window.addEventListener('offline', probe);
    return () => { live = false; clearInterval(every); window.removeEventListener('online', probe); window.removeEventListener('offline', probe); };
  }, []);
  return online;
}

/** Load something from the encrypted store; re-runs whenever the store changes. */
function useStore<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const { version, unlocked } = fs.useField();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (!unlocked) return;
    let live = true;
    load().then((d) => live && setData(d), (e) => live && setError(e));
    return () => { live = false; };
  }, [version, unlocked, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, error };
}

const personName = (p: { firstName: string; lastName: string }) => `${p.lastName}, ${p.firstName}`;
const STATE_LABEL: Record<fs.SyncState, string> = { pending: 'captured — not synced', synced: 'synced', conflict: 'conflict', error: 'sync failed' };

export function PinForm({ mode, onDone, who }: { mode: 'set' | 'unlock'; onDone?: () => void; who?: string }) {
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (mode === 'set' && pin !== pin2) return setErr(new Error('The two PINs do not match.'));
    setBusy(true);
    try {
      if (mode === 'set') await fs.setPin(pin, who ?? 'unknown'); else await fs.unlock(pin);
      onDone?.();
    } catch (x) { setErr(x); setPin(''); setPin2(''); } finally { setBusy(false); }
  };
  const input = (v: string, set: (s: string) => void, label: string, auto = false) => (
    <div className="field"><label htmlFor={`pin-${label}`}>{label}</label>
      <input id={`pin-${label}`} className="pin" type="password" inputMode="numeric" autoComplete="off" maxLength={6} pattern="\d{6}"
        autoFocus={auto} value={v} onChange={(e) => set(e.target.value.replace(/\D/g, '').slice(0, 6))} required /></div>
  );
  return (
    <form onSubmit={submit}>
      {mode === 'set' && <p className="small">Choose a 6-digit field PIN for this iPad. Everything taken offline is encrypted with it, and it
        unlocks field mode when there is no connection. Share it only with the NGF crew using this iPad. <strong>If it is forgotten,
        unsynced work cannot be recovered.</strong></p>}
      {input(pin, setPin, mode === 'set' ? 'New field PIN' : 'Field PIN', true)}
      {mode === 'set' && input(pin2, setPin2, 'Repeat the PIN')}
      <ErrorBox error={err} />
      <button disabled={busy || pin.length !== 6}>{busy ? 'Working…' : mode === 'set' ? 'Set PIN' : 'Unlock'}</button>
    </form>
  );
}

/** "Take offline" on the (online, signed-in) event page: set/unlock the PIN, download the package, cache the app. */
export function TakeOffline({ eventId, me }: { eventId: string; me: string }) {
  const f = fs.useField();
  const [step, setStep] = useState<'idle' | 'pin' | 'working' | 'done'>('idle');
  const [info, setInfo] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => { void fs.init(); }, []);

  const download = useCallback(async () => {
    setStep('working'); setErr(null);
    try {
      const pkg = await get<EventPackage>(`/api/field/events/${eventId}/package`);
      await fs.saveEventPackage(pkg);
      await cacheAppShell();
      setInfo(`${pkg.roster.length} people are on this iPad for ${pkg.event.departmentName}, ${pkg.event.startsOn}.`);
      setStep('done');
    } catch (x) { setErr(x); setStep('idle'); }
  }, [eventId]);

  const start = () => (f.unlocked ? void download() : setStep('pin'));
  return (
    <div className="card field-offline">
      <div className="spread">
        <div>
          <h2 style={{ margin: 0 }}>iPad field mode</h2>
          <div className="muted small">Take this event's roster offline to record assessments with no connection.</div>
        </div>
        {step === 'idle' && <button onClick={start}>Take offline on this iPad</button>}
        {step === 'working' && <span className="badge draft">Downloading…</span>}
      </div>
      {step === 'pin' && (
        <div style={{ maxWidth: 380, marginTop: '0.75rem' }}>
          <PinForm mode={f.hasPin ? 'unlock' : 'set'} who={me} onDone={() => void download()} />
        </div>
      )}
      {step === 'done' && <div className="alert ok" style={{ marginTop: '0.75rem', marginBottom: 0 }}>{info} <Link to={`/field/events/${eventId}`}>Open field mode</Link></div>}
      <ErrorBox error={err} />
    </div>
  );
}

/** Make sure the app itself opens offline: store the page and the files it is running from. */
async function cacheAppShell() {
  if (!('caches' in window)) return;
  const c = await caches.open('ngf-shell-v3');   // same name as public/sw.js
  const urls = new Set(['/', '/index.html', '/ngf-logo.png', '/manifest.webmanifest']);
  document.querySelectorAll<HTMLScriptElement | HTMLLinkElement>('script[src], link[rel="stylesheet"][href], link[rel="modulepreload"][href]')
    .forEach((el) => { const u = new URL('src' in el && el.src ? el.src : (el as HTMLLinkElement).href); if (u.origin === location.origin) urls.add(u.pathname); });
  await c.addAll([...urls]);
}

// ---------------------------------------------------------------- the field app (own shell, no sign-in needed)
export function FieldApp() {
  const f = fs.useField();
  const online = useOnline();
  const [pending, setPending] = useState(0);
  useEffect(() => { void fs.init(); }, []);
  useEffect(() => { if (f.ready) void fs.pendingCount().then(setPending); }, [f.ready, f.version]);

  let body: ReactNode;
  if (!f.ready) body = <p className="muted">Loading…</p>;
  else if (!f.hasPin) body = (
    <div className="card" style={{ maxWidth: 560 }}>
      <h1>Field mode</h1>
      <p>Nothing has been taken offline on this iPad yet. While connected and signed in, open an event under <strong>Events</strong> and
        tap <strong>Take offline on this iPad</strong>.</p>
      {online && <Link className="btn" to="/events">Go to Events</Link>}
    </div>
  );
  else if (!f.unlocked) body = (
    <div className="card" style={{ maxWidth: 420, margin: '2rem auto' }}>
      <h1>Field mode is locked</h1>
      <p className="muted small">PIN set by {f.setBy}. {pending ? `${pending} item(s) waiting to sync.` : ''}</p>
      <PinForm mode="unlock" />
      <ForgotPin pending={pending} />
    </div>
  );
  else body = (
    <Routes>
      <Route index element={<FieldHome online={online} />} />
      <Route path="events/:id" element={<FieldEvent />} />
      <Route path="events/:id/capture/:who" element={<FieldCapture />} />
      <Route path="*" element={<p className="muted">Page not found. <Link to="/field">Field mode home</Link></p>} />
    </Routes>
  );

  return (
    <>
      <DevBanner />
      <header className="topbar field-bar">
        <Link to="/field"><img src="/ngf-logo.png" alt="NGF" /></Link>
        <strong>Field mode</strong>
        <span className={`net-pill ${online ? 'on' : 'off'}`} role="status" aria-live="polite">
          <i />{online ? 'Online' : 'Offline'}
        </span>
        {pending > 0 && <span className="net-pending">{pending} waiting to sync</span>}
        <span style={{ flex: 1 }} />
        {f.unlocked && <button className="secondary" onClick={() => fs.lock()}>Lock</button>}
        {online
          ? <Link to="/" className="btn">Office app</Link>
          : <button disabled title="The office app needs a connection">Office app</button>}
      </header>
      {!online && <div className="net-banner" role="alert">
        <strong>You are offline.</strong> Keep working: captures are saved (encrypted) on this iPad and sync when you are back online.
      </div>}
      <main>{body}</main>
    </>
  );
}

function ForgotPin({ pending }: { pending: number }) {
  const erase = async () => {
    const warn = pending
      ? `Erase field mode on this iPad? ${pending} capture(s) have NOT been synced and will be lost for good.\n\nType ERASE to confirm.`
      : 'Erase field mode on this iPad? Everything taken offline is removed; you can take events offline again.\n\nType ERASE to confirm.';
    if (window.prompt(warn) === 'ERASE') await fs.eraseAll();
  };
  return <p className="small" style={{ marginTop: '1rem' }}><button className="link" onClick={() => void erase()}>Forgot the PIN?</button></p>;
}

// ---------------------------------------------------------------- home: events on this iPad + sync
function FieldHome({ online }: { online: boolean }) {
  const events = useStore(fs.listEvents);
  const captures = useStore(() => fs.listCaptures());
  const waiting = useStore(() => fs.pendingCount());
  const [msg, setMsg] = useState<ReactNode>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const sync = useCallback(async (quiet = false) => {
    setBusy(true); setErr(null); if (!quiet) setMsg(null);
    try {
      const r = await fs.syncNow();
      if (r.synced || r.failed || !quiet) setMsg(`${r.synced} synced${r.failed ? `, ${r.failed} need attention` : ''}.`);
    } catch (x) {
      if (x instanceof fs.NeedsSignIn) setMsg(<>Sign in to sync: <a href="/">open the office app</a>, sign in, then come back to <strong>Field</strong>.</>);
      else if (x instanceof fs.Offline) { if (!quiet) setMsg('No connection — captures stay safely on this iPad until you are back online.'); }
      else setErr(x);
    } finally { setBusy(false); }
  }, []);
  // Try quietly whenever this page opens online or the connection comes back.
  useEffect(() => { if (online) void sync(true); }, [online, sync]);

  const byEvent = (id: string) => (captures.data ?? []).filter((c) => c.eventId === id);
  const pending = waiting.data ?? 0;
  const remove = async (id: string) => {
    if (!window.confirm('Remove this event from the iPad?')) return;
    try { await fs.removeEvent(id); } catch (x) { setErr(x); }
  };

  return (
    <>
      <div className="card spread">
        <div>
          <h1 style={{ margin: 0 }}>Events on this iPad</h1>
          <div className="muted small">Encrypted on this iPad; removed automatically {fs.PACKAGE_DAYS} days after download once everything is synced.</div>
        </div>
        <button onClick={() => void sync()} disabled={busy || !online}>{busy ? 'Syncing…' : pending ? `Sync ${pending} item${pending === 1 ? '' : 's'} now` : 'Sync now'}</button>
      </div>
      {msg && <div className="alert info">{msg}</div>}
      <ErrorBox error={events.error ?? captures.error ?? err} />
      {!events.data ? <p className="muted">Loading…</p> : !events.data.length ? <p className="muted">No events on this iPad.</p> : (
        <div className="field-events">{events.data.map((e) => {
          const cs = byEvent(e.event.id);
          return (
            <div key={e.event.id} className="card">
              <Link to={`/field/events/${e.event.id}`} className="field-event-link">
                <h2 style={{ margin: 0 }}>{e.event.departmentName}</h2>
                <div className="muted">{e.event.startsOn}{e.event.location ? ` · ${e.event.location}` : ''}</div>
                <div className="small" style={{ marginTop: '0.4rem' }}>{e.roster.length} on roster · {cs.length} captured on this iPad
                  {cs.some((c) => c.state !== 'synced') && <> · <strong>{cs.filter((c) => c.state !== 'synced').length} not synced</strong></>}</div>
              </Link>
              <div className="small muted" style={{ marginTop: '0.4rem' }}>Downloaded {new Date(e.downloadedAt).toLocaleString()} ·{' '}
                <button className="link" onClick={() => void remove(e.event.id)}>Remove from iPad</button></div>
            </div>
          );
        })}</div>
      )}
    </>
  );
}

// ---------------------------------------------------------------- one event: roster, walk-ins
function FieldEvent() {
  const { id } = useParams();
  const pkg = useStore(() => fs.getEvent(id!), [id]);
  const captures = useStore(() => fs.listCaptures(id), [id]);
  const walkins = useStore(() => fs.listWalkins(id), [id]);
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const nav = useNavigate();

  if (pkg.error) return <ErrorBox error={pkg.error} />;
  if (!pkg.data) return <p className="muted">{pkg.data === null ? 'Loading…' : 'This event is not on the iPad.'}</p>;
  const e = pkg.data;
  const capOf = (key: string) => (captures.data ?? []).find((c) => c.participantId === key || c.participantClientId === key);
  const orgs = e.event.organizations ?? [];
  const people: { key: string; name: string; employeeId: string | null; dob: string | null; office: string | null; walkin?: Walkin; org: string | null; makeup?: boolean }[] = [
    ...(walkins.data ?? []).map((w) => ({ key: `w-${w.clientId}`, name: `${personName(w)} (walk-in)`, employeeId: w.employeeId, dob: w.dob, office: null, walkin: w,
      org: orgs.find((o) => o.id === w.departmentId)?.name ?? orgs.find((o) => o.host)?.name ?? null })),
    ...e.roster.map((p) => ({ key: p.id, name: personName(p), employeeId: p.employeeId, dob: p.dob, office: p.assessmentStatus, org: p.orgName ?? null, makeup: p.makeup })),
  ];
  const several = orgs.length > 1 || e.roster.some((p) => p.makeup);
  const q = filter.trim().toLowerCase();
  const shown = people.filter((p) => !q || `${p.name} ${p.employeeId ?? ''}`.toLowerCase().includes(q));
  const done = people.filter((p) => capOf(p.walkin ? p.walkin.clientId : p.key)).length;

  return (
    <>
      <p><Link to="/field">← Events on this iPad</Link></p>
      <div className="card">
        <h1 style={{ margin: 0 }}>{e.event.departmentName}</h1>
        {orgs.length > 1 && <p style={{ margin: '2px 0 0' }}>with {orgs.filter((o) => !o.host).map((o) => o.name).join(', ')}</p>}
        <p className="muted" style={{ marginBottom: 0 }}>{e.event.startsOn}{e.event.location ? ` · ${e.event.location}` : ''} · {done} of {people.length} captured on this iPad</p>
      </div>
      <div className="card">
        <div className="spread">
          <input className="field-search" type="search" aria-label="Find a person" placeholder="Find by name or ID #" value={filter} onChange={(x) => setFilter(x.target.value)} />
          <button className="secondary" onClick={() => setAdding(!adding)}>{adding ? 'Cancel walk-in' : 'Add walk-in'}</button>
        </div>
        {adding && <WalkinForm eventId={e.event.id} orgs={orgs} onAdded={(w) => { setAdding(false); nav(`/field/events/${e.event.id}/capture/w-${w.clientId}`); }} />}
        <table className="field-roster" style={{ marginTop: '0.75rem' }}>
          <thead><tr><th>Name</th>{several && <th>Organization</th>}<th>ID #</th><th>DOB</th><th>Status</th></tr></thead>
          <tbody>{shown.map((p) => {
            const c = capOf(p.walkin ? p.walkin.clientId : p.key);
            return (
              <tr key={p.key} className="link" onClick={() => nav(`/field/events/${e.event.id}/capture/${p.key}`)}>
                <td><strong>{p.name}</strong></td>
                {several && <td className="small">{p.org ?? '—'}{p.makeup && <> <span className="badge">make-up</span></>}</td>}
                <td>{p.employeeId ?? '—'}</td>
                <td>{p.dob ?? <span className="badge provisional">no DOB</span>}</td>
                <td>{c ? <span className={`badge ${c.state === 'synced' ? 'synced' : c.state === 'pending' ? 'draft' : 'provisional'}`}>{STATE_LABEL[c.state]}</span>
                  : p.office ? <span className="badge">in office: {p.office}</span> : <span className="muted small">not started</span>}</td>
              </tr>
            );
          })}</tbody>
        </table>
        {!shown.length && <p className="muted">No one matches. Use <strong>Add walk-in</strong> for someone not on the roster.</p>}
      </div>
    </>
  );
}

function WalkinForm({ eventId, orgs, onAdded }: { eventId: string; orgs: { id: string; name: string; host: boolean }[]; onAdded: (w: Walkin) => void }) {
  const [w, setW] = useState({ firstName: '', lastName: '', dob: '', sex: '', employeeId: '', departmentId: '' });
  const [err, setErr] = useState<unknown>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const walkin: Walkin = { clientId: crypto.randomUUID(), eventId, firstName: w.firstName.trim(), lastName: w.lastName.trim(),
        dob: w.dob || null, sex: (w.sex || null) as 'M' | 'F' | null, employeeId: w.employeeId.trim() || null, departmentId: w.departmentId || null,
        createdAt: new Date().toISOString(), state: 'pending' };
      await fs.saveWalkin(walkin);
      onAdded(walkin);
    } catch (x) { setErr(x); }
  };
  const set = (k: keyof typeof w) => (e: { target: { value: string } }) => setW({ ...w, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="walkin" style={{ marginTop: '0.75rem' }}>
      <p className="small muted">Someone not on the roster. The office will check them against existing records when this syncs.</p>
      <div className="row">
        <div className="field"><label htmlFor="wi-first">First name</label><input id="wi-first" value={w.firstName} onChange={set('firstName')} required /></div>
        <div className="field"><label htmlFor="wi-last">Last name</label><input id="wi-last" value={w.lastName} onChange={set('lastName')} required /></div>
      </div>
      <div className="row">
        <div className="field"><label htmlFor="wi-dob">Date of birth</label><input id="wi-dob" type="date" value={w.dob} onChange={set('dob')} required /></div>
        <div className="field"><label htmlFor="wi-sex">Sex</label>
          <select id="wi-sex" value={w.sex} onChange={set('sex')} required><option value="">—</option><option value="M">Male</option><option value="F">Female</option></select></div>
        <div className="field"><label htmlFor="wi-id">Employee ID</label><input id="wi-id" value={w.employeeId} onChange={set('employeeId')} /></div>
      </div>
      {orgs.length > 1 && <div className="field"><label htmlFor="wi-org">Organization</label>
        <select id="wi-org" value={w.departmentId} onChange={set('departmentId')}>
          {orgs.map((o) => <option key={o.id} value={o.host ? '' : o.id}>{o.name}</option>)}</select>
        <span className="small muted">Someone from an organization not listed here: leave the host; the office changes their organization after the sync.</span></div>}
      <ErrorBox error={err} />
      <button>Add and start</button>
    </form>
  );
}

// ---------------------------------------------------------------- capture one person
function FieldCapture() {
  const { id, who } = useParams();
  const fieldState = fs.useField();
  const nav = useNavigate();
  const pkg = useStore(() => fs.getEvent(id!), [id]);
  const captures = useStore(() => fs.listCaptures(id), [id]);
  const walkins = useStore(() => fs.listWalkins(id), [id]);
  const isWalkin = who!.startsWith('w-');
  const walkinId = isWalkin ? who!.slice(2) : null;

  const person: (Pick<RosterPerson, 'firstName' | 'lastName' | 'dob' | 'sex' | 'employeeId'> & { previous: RosterPerson['previous']; office: string | null }) | null = useMemo(() => {
    if (isWalkin) {
      const w = walkins.data?.find((x) => x.clientId === walkinId);
      return w ? { ...w, previous: null, office: null } : null;
    }
    const p = pkg.data?.roster.find((x) => x.id === who);
    return p ? { ...p, office: p.assessmentStatus } : null;
  }, [pkg.data, walkins.data, who, isWalkin, walkinId]);
  const existing = captures.data?.find((c) => (isWalkin ? c.participantClientId === walkinId : c.participantId === who)) ?? null;

  const [m, setM] = useState<Measurements>({});
  const [assessedOn, setAssessedOn] = useState(localToday());
  const [dirty, setDirty] = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => {
    if (captures.data && loadedFor !== who) {
      setLoadedFor(who!);
      if (existing) { setM(existing.measurements); setAssessedOn(existing.assessedOn); }
    }
  }, [captures.data, existing, who, loadedFor]);

  const live = useMemo(() => scoreLive(person?.sex ?? null, person?.dob ?? null, assessedOn, m), [person, assessedOn, m]);
  if (pkg.error || captures.error) return <ErrorBox error={pkg.error ?? captures.error} />;
  if (!pkg.data || !captures.data || !walkins.data) return <p className="muted">Loading…</p>;
  if (!person) return <p className="muted">Person not found. <Link to={`/field/events/${id}`}>Back to the roster</Link></p>;
  const locked = existing?.state === 'conflict';

  const update = (p: Partial<Measurements>) => { setM((cur) => ({ ...cur, ...p })); setDirty(true); setSaved(null); };
  const save = async (next: boolean) => {
    setErr(null);
    try {
      const c: Capture = {
        clientId: existing?.clientId ?? crypto.randomUUID(), eventId: id!,
        ...(isWalkin ? { participantClientId: walkinId! } : { participantId: who! }),
        name: personName(person), assessedOn, measurements: m,
        capturedAt: new Date().toISOString(), capturedBy: fieldState.setBy ?? '',
        state: 'pending', serverId: existing?.serverId,
      };
      await fs.saveCapture(c);
      setDirty(false);
      if (next) nav(`/field/events/${id}`); else setSaved('Saved on this iPad (encrypted). It will sync when you are back online.');
    } catch (x) { setErr(x); }
  };

  return (
    <>
      <p><Link to={`/field/events/${id}`}>← {pkg.data.event.departmentName} roster</Link></p>
      <div className="card spread">
        <div>
          <h1 style={{ margin: 0 }}>{personName(person)}{isWalkin && <span className="badge" style={{ marginLeft: 8 }}>walk-in</span>}</h1>
          <div className="muted">
            {person.sex === 'M' ? 'Male' : person.sex === 'F' ? 'Female' : 'Sex not recorded'} · DOB {person.dob ?? '—'}
            {live ? ` · age ${live.age.toFixed(2)}` : ''} · ID # {person.employeeId ?? '—'}
          </div>
        </div>
        <div className="actions">
          {existing && <span className={`badge status ${existing.state === 'synced' ? 'synced' : 'draft'}`}>{STATE_LABEL[existing.state]}</span>}
          <button className="secondary" disabled={!dirty || locked} onClick={() => void save(false)}>Save</button>
          <button disabled={locked} onClick={() => void save(true)}>Save &amp; back to roster</button>
        </div>
      </div>
      {person.office && !existing && <div className="alert info">The office already has an assessment for this person at this event ({person.office}). A capture here is kept separately and flagged for the office.</div>}
      {existing?.state === 'conflict' && <div className="alert error">{existing.error} This capture cannot be changed on the iPad any more.</div>}
      {existing?.state === 'error' && <div className="alert error">Last sync failed: {existing.error}. It will be retried.</div>}
      {existing?.state === 'synced' && dirty && <div className="alert info">Already synced. Saving sends your changes again (as long as the office has not reviewed it yet).</div>}
      {(!person.sex || !person.dob) && <div className="alert info">Date of birth and sex are needed to calculate results.</div>}
      {saved && <div className="alert ok">{saved}</div>}
      <ErrorBox error={err} />
      <div className="grid2">
        <div>
          <fieldset><legend>Visit</legend>
            <div className="row">
              <div className="field"><label htmlFor="fc-date">Assessment date</label>
                <input id="fc-date" type="date" value={assessedOn} disabled={locked} onChange={(e) => { setAssessedOn(e.target.value); setDirty(true); }} /></div>
              <div className="field muted small" style={{ alignSelf: 'center' }}>
                {person.previous ? `Previous assessment: ${person.previous.assessedOn}` : 'No previous assessment on file.'}</div>
            </div>
          </fieldset>
          <MeasurementFields m={m} update={update} editable={!locked} sex={person.sex} prev={person.previous?.measurements ?? {}} />
          <div className="actions" style={{ marginBottom: '2rem' }}>
            <button disabled={locked} onClick={() => void save(true)}>Save &amp; back to roster</button>
          </div>
        </div>
        <div className="sticky">
          <Results live={live?.r ?? null} prev={person.previous?.results ?? null} prevDate={person.previous?.assessedOn} label="calculated on this iPad" />
        </div>
      </div>
    </>
  );
}
